/**
 * Ops Agent Console — Super-Admin "AI Console"
 *
 * A polished chat surface over the internal operations Foundry agents
 * (Observability, Documentation, Support, Marketing, Product Expert, Product
 * Analyst). The agents' tools execute server-side via the MCP server, so answers
 * contain REAL data — and tool runs are surfaced as inline "tool step" cards so
 * the operator can see them happen.
 */
import { memo, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  BookText,
  LifeBuoy,
  Megaphone,
  GraduationCap,
  BarChart3,
  Bot,
  Send,
  Square,
  Sparkles,
  Trash2,
  RotateCcw,
  ChevronDown,
  Check,
  AlertCircle,
  Loader2,
  Database,
  Wrench,
} from 'lucide-react';
import { cn, Button, Skeleton } from '@respectly/ui';
import { useAuth } from '@respectly/utils/auth';
import {
  useOpsAgentStore,
  type OpsAgentSummary,
  type OpsChatMessage,
  type OpsToolStep,
} from '../../stores/useOpsAgentStore';
import { Markdown } from '../../components/ops-agents/Markdown';

const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  Activity,
  BookText,
  LifeBuoy,
  Megaphone,
  GraduationCap,
  BarChart3,
};
const iconFor = (name: string) => ICONS[name] ?? Bot;

// ─── Tool-step card ───────────────────────────────────────────────────────────

const TOOL_LABELS: Record<string, string> = {
  query_app_insights: 'Queried App Insights',
  read_repo_file: 'Read repository file',
  list_repo_dir: 'Listed repository directory',
  search_knowledge_base: 'Searched knowledge base',
  list_knowledge_docs: 'Listed knowledge docs',
  read_knowledge_doc: 'Read knowledge doc',
  query_database: 'Queried production database',
  list_tables: 'Listed database tables',
  get_table_schema: 'Read table schema',
};

const DB_TOOLS = new Set(['query_app_insights', 'query_database', 'list_tables', 'get_table_schema']);

function toolLabel(step: OpsToolStep): string {
  const base = step.tool ? TOOL_LABELS[step.tool] ?? step.tool : 'Tool call';
  return step.summary ? `${base} · ${step.summary}` : base;
}

function ToolStepCard({ step }: { step: OpsToolStep }) {
  const [open, setOpen] = useState(false);
  const ToolIcon = step.tool && DB_TOOLS.has(step.tool) ? Database : Wrench;

  return (
    <div className="my-1.5 overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-zinc-700 dark:text-zinc-300"
      >
        <ToolIcon className="h-3.5 w-3.5 shrink-0 text-violet-500" />
        <span className="flex-1 truncate">{toolLabel(step)}</span>
        {step.status === 'running' && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
        {step.status === 'completed' && <Check className="h-3.5 w-3.5 text-emerald-500" />}
        {step.status === 'failed' && <AlertCircle className="h-3.5 w-3.5 text-red-500" />}
        <ChevronDown className={cn('h-3.5 w-3.5 text-zinc-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="border-t border-zinc-200 px-3 py-2 text-[11px] text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
          {step.serverLabel && <div>server: {step.serverLabel}</div>}
          {step.tool && <div>tool: {step.tool}</div>}
          <div>status: {step.status}</div>
          {step.summary && <div>result: {step.summary}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Message bubble ─────────────────────────────────────────────────────────────

const MessageBubble = memo(function MessageBubble({ message }: { message: OpsChatMessage }) {
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start')}>
      <div className={cn('max-w-[85%] min-w-0', isUser && 'flex flex-col items-end')}>
        {!isUser && message.toolSteps && message.toolSteps.length > 0 && (
          <div className="mb-1.5">
            {message.toolSteps.map((s, i) => (
              <ToolStepCard key={s.id || i} step={s} />
            ))}
          </div>
        )}
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 text-sm',
            isUser
              ? 'bg-violet-600 text-white'
              : 'border border-zinc-200 bg-white text-zinc-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100'
          )}
        >
          {isUser ? (
            <span className="whitespace-pre-wrap break-words">{message.content}</span>
          ) : message.content ? (
            <Markdown content={message.content} streaming={!!message.streaming} />
          ) : message.streaming ? (
            <span className="inline-flex items-center gap-1 text-zinc-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
});

// ─── Empty state ─────────────────────────────────────────────────────────────────

function EmptyState({ agent, onPick }: { agent: OpsAgentSummary; onPick: (prompt: string) => void }) {
  const Icon = iconFor(agent.icon);
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/20">
        <Icon className="h-7 w-7" />
      </div>
      <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-50">{agent.label}</h2>
      <p className="mt-1 max-w-md text-sm text-zinc-500 dark:text-zinc-400">{agent.description}</p>
      <div className="mt-6 grid w-full max-w-xl gap-2 sm:grid-cols-2">
        {agent.samplePrompts.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPick(p)}
            className="rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left text-sm text-zinc-700 transition-colors hover:border-violet-300 hover:bg-violet-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-violet-700 dark:hover:bg-violet-950/30"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function OpsAgentConsole() {
  const { getAccessToken } = useAuth();
  // Select only what this page renders. Crucially `convo` resolves to the ACTIVE
  // conversation, so a different agent streaming in the background (whose entry in
  // `conversations` changes ~60×/s) does not re-render the foreground. Actions are
  // stable refs, so including them never triggers a render.
  const {
    agents,
    agentsLoaded,
    agentsError,
    activeAgentKey,
    convo,
    loadAgents,
    selectAgent,
    sendMessage,
    stop,
    clearConversation,
  } = useOpsAgentStore(
    useShallow((s) => ({
      agents: s.agents,
      agentsLoaded: s.agentsLoaded,
      agentsError: s.agentsError,
      activeAgentKey: s.activeAgentKey,
      convo: s.activeAgentKey ? s.conversations[s.activeAgentKey] : undefined,
      loadAgents: s.loadAgents,
      selectAgent: s.selectAgent,
      sendMessage: s.sendMessage,
      stop: s.stop,
      clearConversation: s.clearConversation,
    }))
  );

  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Whether the operator is parked at the bottom. A ref (not state) so scrolling
  // never triggers a re-render.
  const atBottomRef = useRef(true);

  useEffect(() => {
    if (!agentsLoaded) void loadAgents();
  }, [agentsLoaded, loadAgents]);

  const activeAgent = useMemo(
    () => agents.find((a) => a.key === activeAgentKey) ?? null,
    [agents, activeAgentKey]
  );
  const messages = convo?.messages ?? [];
  const isStreaming = convo?.status === 'streaming';
  const lastUserMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]!.role === 'user') return messages[i]!.content;
    }
    return '';
  }, [messages]);

  // Track whether the operator is near the bottom so we don't yank them back down
  // while they scroll up to read mid-stream.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      atBottomRef.current = dist < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Force-follow when switching agents.
  useEffect(() => {
    atBottomRef.current = true;
  }, [activeAgentKey]);

  // Auto-scroll to the newest content as it streams — instant while streaming
  // (smooth queues an animation per flush and thrashes layout), and only when the
  // operator is already parked at the bottom. Runs once per flush (already throttled).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !atBottomRef.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: isStreaming ? 'auto' : 'smooth' });
  }, [messages, activeAgentKey, isStreaming]);

  const submit = (text: string) => {
    if (!activeAgentKey || !text.trim() || isStreaming) return;
    void sendMessage(activeAgentKey, text, getAccessToken);
    setDraft('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(draft);
    }
  };

  return (
    <motion.div
      className="h-[calc(100vh-theme(spacing.16))] flex flex-col bg-zinc-50 dark:bg-zinc-950"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Header */}
      <div className="shrink-0 border-b border-zinc-200 bg-white px-5 dark:border-zinc-800 dark:bg-zinc-900">
        <div className="flex h-14 items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">AI Console</h1>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                {activeAgent ? activeAgent.label : 'Internal operations agents'}
              </p>
            </div>
          </div>
          {activeAgentKey && messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => clearConversation(activeAgentKey)}
              className="h-8 gap-1.5 text-zinc-500"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1">
        {/* Agent switcher */}
        <aside className="hidden w-72 shrink-0 flex-col overflow-y-auto border-r border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900 md:flex">
          <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Agents</div>
          {!agentsLoaded ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : agentsError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-600 dark:border-red-900 dark:bg-red-950/40">
              {agentsError}
              <button className="mt-2 flex items-center gap-1 font-medium" onClick={() => loadAgents()}>
                <RotateCcw className="h-3 w-3" /> Retry
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              {agents.map((agent) => {
                const Icon = iconFor(agent.icon);
                const active = agent.key === activeAgentKey;
                return (
                  <button
                    key={agent.key}
                    type="button"
                    onClick={() => selectAgent(agent.key)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors',
                      active
                        ? 'border-violet-200 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/40'
                        : 'border-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800/60'
                    )}
                  >
                    <div
                      className={cn(
                        'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        active ? 'bg-violet-600 text-white' : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className={cn('text-sm font-medium', active ? 'text-violet-900 dark:text-violet-200' : 'text-zinc-800 dark:text-zinc-200')}>
                        {agent.label}
                      </div>
                      <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-zinc-500 dark:text-zinc-400">
                        {agent.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        {/* Chat */}
        <section className="flex min-w-0 flex-1 flex-col">
          <div ref={scrollRef} className="flex-1 overflow-y-auto">
            {!activeAgent ? (
              <div className="flex h-full items-center justify-center text-sm text-zinc-400">
                {agentsLoaded ? 'Select an agent to start.' : <Loader2 className="h-5 w-5 animate-spin" />}
              </div>
            ) : convo?.status === 'loading' ? (
              <div className="mx-auto max-w-3xl space-y-3 p-5">
                <Skeleton className="h-16 w-2/3 rounded-2xl" />
                <Skeleton className="ml-auto h-12 w-1/2 rounded-2xl" />
              </div>
            ) : messages.length === 0 ? (
              <EmptyState agent={activeAgent} onPick={submit} />
            ) : (
              <div className="mx-auto max-w-3xl space-y-4 p-5">
                {messages.map((m, i) => (
                  <MessageBubble key={i} message={m} />
                ))}
              </div>
            )}
          </div>

          {/* Error banner */}
          <AnimatePresence>
            {convo?.error && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mx-auto flex w-full max-w-3xl items-center gap-2 px-5 py-2 text-xs text-red-600"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                <span className="flex-1">{convo.error}</span>
                <button className="font-medium underline" onClick={() => submit(lastUserMessage)}>
                  Retry
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Composer */}
          {activeAgent && (
            <div className="shrink-0 border-t border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
              <div className="mx-auto flex max-w-3xl items-end gap-2">
                <textarea
                  ref={textareaRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={1}
                  placeholder={`Message ${activeAgent.label}…`}
                  disabled={isStreaming}
                  className="max-h-40 min-h-[44px] flex-1 resize-none rounded-xl border border-zinc-300 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none transition-colors placeholder:text-zinc-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:ring-violet-900/40"
                />
                {isStreaming ? (
                  <Button onClick={() => activeAgentKey && stop(activeAgentKey)} variant="secondary" className="h-11 gap-1.5">
                    <Square className="h-4 w-4" /> Stop
                  </Button>
                ) : (
                  <Button onClick={() => submit(draft)} disabled={!draft.trim()} className="h-11 gap-1.5">
                    <Send className="h-4 w-4" /> Send
                  </Button>
                )}
              </div>
              <p className="mx-auto mt-1.5 max-w-3xl px-1 text-[11px] text-zinc-400">
                Enter to send · Shift+Enter for a new line. Tools run live against production — answers reflect real data.
              </p>
            </div>
          )}
        </section>
      </div>
    </motion.div>
  );
}

export default OpsAgentConsole;
